import React from 'react';
import { Modal, Button, Typography } from 'antd';
import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const SessionWarningModal = ({
  isOpen,
  timeRemaining,
  onExtend,
  onDecline,
  maxExtensionsReached,
  extensionsUsed = 0,
  maxExtensions = 0,
  isExtending = false,
  extendError = null,
}) => {
  const secondsRemaining = Math.floor(timeRemaining / 1000);
  
  const pulseKeyframes = `
    @keyframes pulse-warning {
      0% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.05); opacity: 0.8; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes slide-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .modern-modal .ant-modal-content {
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    }
  `;

  return (
    <>
      <style>{pulseKeyframes}</style>
      <Modal
        open={isOpen}
        onCancel={onDecline}
        closable={false}
        centered
        maskClosable={false}
        footer={null}
        width={400}
        className="modern-modal"
        styles={{ body: { padding: '32px 24px' } }}
      >
        <div style={{ textAlign: "center", animation: 'slide-up 0.4s ease-out' }}>
          
          {maxExtensions > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {Array.from({ length: maxExtensions }).map((_, i) => {
                  let dotColor = '#cbd5e1'; // unused (grey)
                  if (i < extensionsUsed) dotColor = '#ef4444'; // used (red)
                  else if (i === extensionsUsed && !maxExtensionsReached) dotColor = '#f59e0b'; // current (orange)
                  else if (i === extensionsUsed && maxExtensionsReached) dotColor = '#ef4444'; // edge case fallback
                  
                  return (
                    <div 
                      key={i} 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        borderRadius: '50%', 
                        backgroundColor: dotColor,
                        boxShadow: i === extensionsUsed && !maxExtensionsReached ? '0 0 10px rgba(245, 158, 11, 0.6)' : 'none',
                        transition: 'all 0.3s ease'
                      }} 
                    />
                  );
                })}
              </div>
              <Text style={{ color: '#6b7280', fontSize: '15px', fontWeight: '600' }}>
                {Math.min(extensionsUsed, maxExtensions)}/{maxExtensions}
              </Text>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            {maxExtensionsReached ? (
              <ExclamationCircleOutlined style={{ fontSize: '56px', color: '#ef4444' }} />
            ) : (
              <ClockCircleOutlined 
                style={{ 
                  fontSize: '56px', 
                  color: '#f59e0b',
                  animation: secondsRemaining <= 10 ? 'pulse-warning 1s infinite' : 'none'
                }} 
              />
            )}
          </div>

          <Title level={4} style={{ marginTop: 0, marginBottom: '8px', color: '#111827', fontWeight: '600' }}>
            {maxExtensionsReached ? 'Session Expiring Soon' : 'Are you still there?'}
          </Title>

          {!maxExtensionsReached && maxExtensions > 0 && (
            <Text style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>
              Extension {Math.min(extensionsUsed + 1, maxExtensions)} of {maxExtensions} available
            </Text>
          )}

          <div style={{ 
            background: '#f9fafb', 
            border: '1px solid #e5e7eb',
            borderRadius: '12px', 
            padding: '16px',
            marginBottom: '24px',
            marginTop: '20px'
          }}>
            <Text style={{ fontSize: '14px', color: '#6b7280', display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Your booking session will expire in
            </Text>
            <div style={{ 
              fontSize: '36px', 
              fontWeight: '700', 
              color: secondsRemaining <= 10 ? '#ef4444' : '#008385',
              fontFamily: 'monospace',
              lineHeight: '1',
              animation: secondsRemaining <= 10 ? 'pulse-warning 1s infinite' : 'none'
            }}>
              00:{secondsRemaining.toString().padStart(2, '0')}
            </div>
          </div>
          
          {maxExtensionsReached ? (
            <Text style={{ display: 'block', marginBottom: '28px', color: '#dc2626', fontSize: '14px', fontWeight: '500' }}>
              Maximum number of session extensions reached. Please complete your booking quickly to secure your slot.
            </Text>
          ) : (
            <Text style={{ display: 'block', marginBottom: '28px', color: '#4b5563', fontSize: '14px' }}>
              We've reserved this slot for you. Do you need more time to complete your booking?
            </Text>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!maxExtensionsReached && (
              <Button 
                onClick={onExtend} 
                type="primary" 
                size="large"
                loading={isExtending}
                disabled={isExtending}
                style={{ 
                  backgroundColor: '#008385', 
                  borderColor: '#008385',
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 6px -1px rgba(0, 131, 133, 0.2)'
                }}
                block
              >
                {isExtending ? 'Extending…' : 'Yes, extend my session'}
              </Button>
            )}
            {extendError && typeof extendError === 'string' && !extendError.includes('Converting circular') && (
              <div style={{ color: '#ef4444', fontSize: '13px', textAlign: 'center', marginTop: '4px' }}>
                {extendError}
              </div>
            )}
            <Button 
              onClick={onDecline} 
              type="default" 
              size="large"
              style={{ 
                height: maxExtensionsReached ? '48px' : '40px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '500',
                border: maxExtensionsReached ? '1px solid #e5e7eb' : 'none',
                background: maxExtensionsReached ? '#f9fafb' : 'transparent',
                color: maxExtensionsReached ? '#374151' : '#6b7280'
              }}
              block
            >
              {maxExtensionsReached ? 'Close & return to booking' : 'No, let it expire'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SessionWarningModal;
